import React, { useState } from "react";
import { Alert, Box, useTheme } from "@mui/material";
import Button from "components/basic/button";
import LoadingSpinner from "components/basic/loadingSpinner";
import Switch from "components/basic/switch";
import Typography from "components/basic/typography";
import { useSnack } from "hooks/useSnack";
import { formatIsoDate } from "pages/formTools";

import ReasonDialog from "../ReasonDialog";
import { useAdminActions, useAdminExpiringVendors } from "../../adminApi";
import { formatDate } from "../../helpers";
import { AdminActionResult, AdminVendor } from "../../types";

const daysText = (daysLeft: number) =>
  daysLeft < 0
    ? `expired ${-daysLeft} day${daysLeft === -1 ? "" : "s"} ago`
    : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`;

/**
 * Approved vendors whose insurance or licence expires within 30 days. The
 * daily job reminds and pauses them on its own; these are the manual controls.
 */
const ExpiringTab: React.FC = () => {
  const theme = useTheme();
  const showSnack = useSnack();
  const { vendors, loading, error, refresh } = useAdminExpiringVendors();
  const { sendExpiryReminder, setExpiryReminder, setVendorPaused } =
    useAdminActions();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [pausing, setPausing] = useState<AdminVendor | null>(null);

  const run = async (
    vendorId: number,
    action: Promise<AdminActionResult>,
    done: string,
  ) => {
    setBusyId(vendorId);
    const result = await action;
    setBusyId(null);
    if (!result.ok) {
      showSnack(result.error ?? "Something went wrong", "error");
      return false;
    }
    showSnack(done, "success");
    refresh();
    return true;
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <Box display="flex" flexDirection="column" gap={2} p={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {!error && !vendors.length && (
        <Typography variant="body1" align="center" style={{ opacity: 0.7 }}>
          No documents expire in the next 30 days.
        </Typography>
      )}

      {vendors.map((row) => {
        const v = row.vendor;
        const busy = busyId === row.id;
        const lastSent = [v.insuranceReminderSentAt, v.licenseReminderSentAt]
          .filter((date): date is string => Boolean(date))
          .sort()
          .pop();

        return (
          <Box
            key={row.id}
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: 2,
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
            }}
          >
            <Box flex={1}>
              <Typography variant="body1" style={{ fontWeight: 600 }}>
                {v.businessName}
              </Typography>
              {v.expiringDocuments.map((doc) => (
                <Typography
                  key={doc.kind}
                  variant="body2"
                  style={{
                    color:
                      doc.daysLeft < 0 ? theme.palette.error.main : undefined,
                  }}
                >
                  {`${doc.label}: ${formatIsoDate(doc.expiryDate)} (${daysText(doc.daysLeft)})`}
                </Typography>
              ))}
              <Typography variant="body2" style={{ opacity: 0.7 }}>
                {lastSent
                  ? `Last reminder ${formatDate(lastSent)}`
                  : "No reminder sent yet"}
                {v.pausedAt ? " · Paused" : ""}
              </Typography>
            </Box>

            <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
              <Switch
                label="Banner"
                checked={v.expiryReminderEnabled}
                disabled={busy}
                onChange={(enabled) =>
                  run(
                    row.id,
                    setExpiryReminder(row.id, enabled),
                    enabled ? "Banner shown" : "Banner hidden",
                  )
                }
              />
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() =>
                  run(row.id, sendExpiryReminder(row.id), "Reminder sent")
                }
              >
                Send reminder
              </Button>
              <Button
                size="small"
                color={v.pausedAt ? "primary" : "error"}
                disabled={busy}
                onClick={() =>
                  v.pausedAt
                    ? run(row.id, setVendorPaused(row.id, false), "Resumed")
                    : setPausing(row)
                }
              >
                {v.pausedAt ? "Resume services" : "Pause services"}
              </Button>
            </Box>
          </Box>
        );
      })}

      {pausing && (
        <ReasonDialog
          title={`Pause ${pausing.vendor.businessName}?`}
          body="Their services are hidden, their portal turns off and new quotes are blocked until you resume them. They are emailed this reason."
          confirmLabel="Pause"
          loading={busyId === pausing.id}
          onConfirm={async (reason) => {
            if (
              await run(
                pausing.id,
                setVendorPaused(pausing.id, true, reason),
                "Vendor paused and emailed",
              )
            ) {
              setPausing(null);
            }
          }}
          onClose={() => setPausing(null)}
        />
      )}
    </Box>
  );
};

export default ExpiringTab;
